/**
 * Plans at the project's home (D215 round 2): what one machine kept in `.mojoup/plans/` is the
 * project's, so approving on any machine is the same approval and every member sees the plan.
 * A plan is a project record (projectRecords.ts) under its repository; the workbench's Plan
 * (src/coordination/plan.ts) is carried loosely with its checked fields below. AIOE's own
 * contract; the workbench copies it into `shared/schemas/plans.ts`.
 *
 *   GET    /projects/v1/:id/roots/:rootId/plans?status=&since=&limit=   members: { seq, plans[] }
 *   PUT    /projects/v1/:id/roots/:rootId/plans/:planId                contributors and above: create or replace; If-Match
 *   GET    /projects/v1/:id/roots/:rootId/plans/:planId
 *   DELETE /projects/v1/:id/roots/:rootId/plans/:planId                owners, or its author while it is a draft
 *   POST   /projects/v1/:id/roots/:rootId/plans/:planId/approve        { note? }  contributors and above; 409 when not a draft
 *   POST   /projects/v1/:id/roots/:rootId/plans/:planId/abandon        { reason }  the same; from any status but done or verified
 *
 * AIOE 0.21.0 (every addition optional; a workbench that ignores them is unchanged):
 *
 *   GET    /projects/v1/:id/plans?status=&rootId=&limit=   members: { seq, plans[] } across every repository and the project's
 *                                                         own (`rootId` narrows to one; "" = the project's own), each with
 *                                                         its `rootId` and `status`, newest change first
 *   PUT|GET|DELETE /projects/v1/:id/plans/:planId, POST …/approve, POST …/abandon
 *                                                         the project's own plans (root ""), as the routes above
 *   POST   /projects/v1/:id/plans/number   { planId, rootId, teamId, prefer? }
 *                                                         contributors and above, a sign-in or their desktop; a node names the person in
 *                                                         `x-aioe-principal` (as the board routes): the plan's number in its sequence →
 *                                                         201 `{ planId, number, ref: "PLAN-<n>", takenBy, at }` for a new number; 200 with
 *                                                         the same body when that plan was numbered before (a retry gets the same number);
 *                                                         404 `{ error: "project_not_found" }`. One sequence per (project, rootId, teamId);
 *                                                         its high-water mark never goes back, deleted plans included. `prefer` is kept
 *                                                         when its number is above every number taken in the sequence, else the next
 *                                                         one above the mark is given. Audited `plan.number`
 *
 * AIOE 0.35.0 (the workbench's D247, C-8; every addition optional):
 *
 *   POST …/plans/:planId/approve   may carry `{ queuedAfter: { planId, rootId? } }`: "Queue after" another team's plan that serves one of
 *                                  its roadmap rows. While that plan is live the plan stays a draft, with `queuedAfter` (`PlanRowHold`,
 *                                  projectMap.ts) recorded on it and audited `plan.queue`; it is approved automatically when that plan is
 *                                  done, verified, abandoned or removed (audited `plan.approve` with `queuedAfter` and `auto: true`; its
 *                                  author is told). 400 `unknown_plan` when the plan to wait for is not the project's. When that plan is not
 *                                  live already, the plan is approved at once. A person approving a queued draft by hand clears the wait.
 *   PUT …/plans/:planId            with `status: "approved"` and `queuedAfter` of a live plan stores a draft (the same wait); with
 *                                  `status: "draft"` and `queuedAfter` it is kept as sent; `rowAlongside[]` is kept as sent (the home adds to it
 *                                  when a person works alongside, `POST …/claims` with `alongside`, working.ts).
 *   `planQueuedWords(plan)`        "waiting for PLAN-1 to finish" for a draft with `queuedAfter`.
 *
 * A plan step (`nodes[]`) may carry `status` (todo, doing, done, blocked), so a wave reads as done
 * without its tasks.
 *
 * Words for people: plan, draft, approved, abandoned, PLAN-3.
 */
import { z } from "zod";
import { planRowAlongsideSchema, planRowHoldSchema } from "./projectMap.js";
export const PLANS_SCHEMA_VERSION = 1;
export const PLAN_RECORD_STATUSES = ["draft", "approved", "dispatched", "done", "verified", "abandoned"];
export const planRecordStatusSchema = z.enum(PLAN_RECORD_STATUSES);
const id = z.string().min(1).max(200);
/** A plan step's own progress (0.21.0, optional): the phone and the console show a wave as done when its steps are. */
export const PLAN_STEP_STATUSES = ["todo", "doing", "done", "blocked"];
export const planStepStatusSchema = z.enum(PLAN_STEP_STATUSES);
export const planRecordNodeSchema = z.looseObject({
    ref: z.string().min(1).max(100),
    title: z.string().max(500),
    /**
     * 0.21.0, optional: where the step stands, one of PLAN_STEP_STATUSES. Another word (a workbench's
     * own step states) is kept as written, never refused, and reads as not said (`planStepStatus`).
     */
    status: z.union([planStepStatusSchema, z.string().max(40)]).optional(),
    taskId: z.string().max(100).optional(),
    assignee: z.string().max(200).optional(),
    role: z.string().max(200).optional(),
    dependsOn: z.array(z.string().max(100)).max(200).default([]),
    repository: z.looseObject({ rootId: z.string().max(200), name: z.string().max(200).optional() }).optional(),
    task: z.looseObject({ rootId: z.string().max(200), board: z.string().max(200), uid: z.string().max(200) }).optional(),
});
export const planPutSchema = z.looseObject({
    id,
    ref: z.string().max(50).optional(),
    teamId: z.string().min(1).max(200),
    objective: z.string().max(4000),
    summary: z.string().max(20_000).optional(),
    status: planRecordStatusSchema,
    author: z.string().max(200),
    createdAt: z.string().max(50),
    updatedAt: z.string().max(50),
    nodes: z.array(planRecordNodeSchema).max(500).default([]),
    /** A criterion: the workbench sends title, given, when and then; `text` is the older one-line form. Either reads. */
    acceptance: z.array(z.looseObject({ id: z.string().max(100), text: z.string().max(2000).optional(), title: z.string().max(500).optional(), then: z.string().max(2000).optional() })).max(200).optional(),
    roadmap: z.array(z.looseObject({ rootId: z.string().max(200), id: z.string().max(100) })).max(100).optional(),
    branch: z.string().max(300).optional(),
    base: z.string().max(300).optional(),
    projectTask: z.looseObject({ id: z.string().max(100), uid: z.string().max(200) }).optional(),
    briefId: z.string().max(200).optional(),
    /** Waiting for another team's plan (AIOE 0.35.0, D247): a draft until that plan finishes. */
    queuedAfter: planRowHoldSchema.optional(),
    /** Worked alongside another team's plan on these rows, by a person's choice (AIOE 0.35.0, D247). */
    rowAlongside: z.array(planRowAlongsideSchema).max(50).optional(),
    intentId: z.string().max(200).optional(),
    unify: z
        .looseObject({
        at: z.string().max(50),
        summary: z.string().max(20_000),
        tasks: z.looseObject({ planned: z.number().int().nonnegative(), done: z.number().int().nonnegative(), failed: z.number().int().nonnegative(), skipped: z.number().int().nonnegative() }),
        decisions: z.array(z.string().max(200)).max(500).default([]),
        deferred: z.array(z.string().max(500)).max(500).default([]),
        nextAction: z.string().max(2000).optional(),
    })
        .optional(),
});
/** A step's status when it is one of the four; undefined when absent or another word. */
export function planStepStatus(step) {
    return planStepStatusSchema.safeParse(step.status).data;
}
export const planApproveSchema = z.object({
    note: z.string().max(2000).optional(),
    /** Wait for this plan to finish before approving (AIOE 0.35.0, D247, C-8). `rootId` is its repository; searched for when left out. */
    queuedAfter: z.object({ planId: id, rootId: z.string().max(200).optional() }).optional(),
});
export const planAbandonSchema = z.object({ reason: z.string().min(1).max(2000) });
/** `POST /projects/v1/:id/plans/number` (0.21.0): the plan to number, in its repository's and team's sequence. */
export const planNumberRequestSchema = z.object({
    planId: id,
    /** The plan's repository; "" for the project's own plans. */
    rootId: z.string().max(200),
    teamId: z.string().min(1).max(200),
    /** The number the workbench would like (what it gave the plan locally); kept when it is above every number taken. */
    prefer: z.string().regex(/^PLAN-\d{1,9}$/, "PLAN-<n>").optional(),
});
/** The answer, the same on a retry: the plan, its number and label, who took it (the person, and the machine that asked), and when. */
export const planNumberResponseSchema = z.object({
    planId: z.string(),
    number: z.number().int().positive(),
    ref: z.string().regex(/^PLAN-\d+$/),
    takenBy: z.object({ principal: z.string().optional(), deviceId: z.string().optional() }),
    at: z.string(),
});
/** `PLAN-<n>` for a number; the number in a `PLAN-<n>` label, else undefined. */
export const planLabel = (n) => `PLAN-${n}`;
export function planNumberOf(label) {
    const m = /^PLAN-(\d{1,9})$/.exec(label ?? "");
    return m ? Number(m[1]) : undefined;
}
/**
 * Acceptance verdicts at the home (A-19/A-20). A person passes, fails or clears a criterion from
 * the console or the phone:
 *
 *   POST …/plans/:planId/acceptance   { criterionId, verdict: "passed" | "failed" | "unchecked", note? }
 *                                     contributors and above; 404 unknown plan; 409 for an abandoned plan
 *
 * The verdict is kept on the plan as `verdicts[criterionId] = { verdict, by, at, note? }` (a PUT that
 * leaves `verdicts` out keeps the home's). A criterion with no verdict is "not yet checked", never a pass.
 * A workbench's own `checks[]` (status passed, failed, pending, unverified) read the same way.
 */
export const ACCEPTANCE_VERDICTS = ["passed", "failed", "unchecked"];
export const acceptanceVerdictSchema = z.enum(ACCEPTANCE_VERDICTS);
export const planAcceptanceRequestSchema = z.object({ criterionId: z.string().min(1).max(100), verdict: acceptanceVerdictSchema, note: z.string().max(2000).optional() });
/** One criterion's verdict: a person's (`verdicts`), else the workbench's check, else not yet checked. */
export function planCriterionVerdict(plan, criterionId) {
    const p = plan;
    const v = acceptanceVerdictSchema.safeParse(p.verdicts?.[criterionId]?.verdict);
    if (v.success)
        return v.data;
    const s = p.checks?.find((c) => c.id === criterionId)?.status;
    return s === "passed" ? "passed" : s === "failed" ? "failed" : "unchecked";
}
/** The words for a verdict. */
export const ACCEPTANCE_VERDICT_TEXT = { passed: "Passed", failed: "Failed", unchecked: "Not yet checked" };
/** The plan statuses that are over: a plan waiting for one of these is released. */
export const PLAN_FINISHED_STATUSES = ["done", "verified", "abandoned"];
/** "waiting for PLAN-1 to finish" for a draft with `queuedAfter`; undefined for any other plan. */
export function planQueuedWords(plan) {
    if (plan.status !== "draft" || !plan.queuedAfter)
        return undefined;
    return `waiting for ${plan.queuedAfter.planRef ?? "another plan"} to finish`;
}
//# sourceMappingURL=plans.js.map